import {action, computed, observable} from "mobx";
import * as moment from "moment-timezone";
import {Photo} from "../../components/Photo";
import {SolutionType, SolutionTypeType} from "../SolutionDataSource";
import {IBadgable} from "./IBadgable";
import {DateTimeHelpers} from "shared/DateTimeHelpers";
import {DateTime} from "luxon";
import {BadgeGroup} from "components/badges/BadgeGroup";
import {UIState} from "data/UIState";

const READABLE_DATE_FORMAT = 'MMMM Do';
/**
 * Reduced solution object for the dashboard.
 */
export interface IDashboardSolution extends IBadgable {
  points:number;
  badge:string;
  badgePreview:string;
  badgeEarned:boolean;
  badgeEarnedDate?:Date;
  badgeUsed:string;
  badgeThreshold:number;
  badgeInstructions:string;
  closeDate:string;
  name:string;
  type:string;
  openDate:Date;
  photo:string;
  registered:boolean;
  visibleDate:Date;
  subTitle:string;
  cardSubTitle:string;
  introduction:string;
  alwaysAvailable:boolean;
  externalLink:string;
  isOpen:boolean;
  _id:string;
  maxRegistrationsReached:boolean;
  autoRegister:boolean;
  badgeGroup:string;
  solutionLogo:string;
  registrationDate:Date;
}

export class DashboardSolution implements IDashboardSolution {
  @observable points = 0;
  badge:string;
  badgePreview:string;
  @observable badgeEarned = false;
  @observable badgeEarnedDate;
  badgeUsed:string;
  badgeThreshold:number;
  badgeInstructions:string;
  closeDate:string;
  name:string;
  type:SolutionTypeType;
  openDate:Date;
  photo:string;
  registered:boolean;
  visibleDate:Date;
  subTitle:string;
  cardSubTitle:string;
  introduction:string;
  alwaysAvailable:boolean;
  externalLink:string;
  isOpen:boolean;
  _id:string;
  @observable maxRegistrationsReached:boolean;
  autoRegister:boolean;
  photoObject:Photo;
  badgeGroup:string;
  solutionLogo:string;
  @observable registrationDate:Date;

  @computed get badgeType():IBadgeType {
    for(let i=0;i<badgeTypeArray.length;i++) {
      if(-1!=this.badge.indexOf(badgeTypeArray[i].match)) {
        return badgeTypeArray[i];
      }
    }
    return BadgeTypes.UNKNOWN;
  }

  @computed get badgeGroupId() {
    return this.badgeGroup && UIState.settings.USES_BADGE_GROUPS ? this.badgeGroup : ""+DateTimeHelpers.parseDateTimeOr(this.badgeEarnedDate, DateTime.now()).year;
  }

  @action updatePoints(points) {
    this.points = points;
    // Badge Earned?
    if(this.badgeThreshold && this.points && !this.badgeEarned && this.points >= this.badgeThreshold) {
      this.badgeEarned = true;
      this.badgeEarnedDate = new Date();
    }
  }

  @computed get isVideoOnDemand() {
    return SolutionType.VIDEOS===this.type;
  }
  @computed get isCompanyBooth() {
    return SolutionType.COMPANY_BOOTH===this.type;
  }
  @computed get isDashboardLink() {
    return SolutionType.DASHBOARD_LINK ===this.type;
  }
  @computed get beginsOn() {
    if(this.openDate) {
      let on = "a future date.";
      try {
        on = moment(this.openDate).format(READABLE_DATE_FORMAT);
      } catch(e) {
        console.log(e);
      }
      return "Begins on "+on;
    } else {
      return "Start date not yet set.";
    }
  }



  @computed get beginsOnMonthYear() {
    if(this.openDate) {
      try {
        return moment(this.openDate).format("MMMM yyyy");
      } catch (e) {
        console.log(e);
      }
    }

    return "";
  }

  @computed get readableRegisterLink() {
    if(this.openDate) {
      let on = "";
      try {
        on = " on " + moment(this.openDate).format(READABLE_DATE_FORMAT);
      } catch(e) {
        console.log(e);
      }
      return "Register to participate"+on;
    } else {
      return "";
    }
  }



  constructor(json:IDashboardSolution) {
    this.points = json.points;
    this.badge = json.badge;
    this.badgePreview = json.badgePreview;
    this.badgeEarned = json.badgeEarned;
    this.badgeEarnedDate = json.badgeEarnedDate ? json.badgeEarnedDate : json.openDate;  // HACK: This is to fix badges awarded before badgeEarnedDate  was set
    this.badgeUsed = json.badgeUsed;
    this.badgeThreshold = json.badgeThreshold;
    this.badgeInstructions = json.badgeInstructions;
    this.closeDate = json.closeDate;
    this.name = json.name;
    this.type = json.type as SolutionTypeType;
    this.openDate = json.openDate;
    this.photo = json.photo;
    this.registered = json.registered;
    this.visibleDate = json.visibleDate;
    this._id = json._id;
    this.photoObject = new Photo(this.photo);
    this.subTitle = json.subTitle;
    this.cardSubTitle = json.cardSubTitle;
    this.introduction = json.introduction;
    this.alwaysAvailable = json.alwaysAvailable;
    this.externalLink = json.externalLink;
    this.isOpen = json.isOpen;
    this.maxRegistrationsReached = json.maxRegistrationsReached;
    this.autoRegister = json.autoRegister;
    this.badgeGroup = json.badgeGroup;
    this.registrationDate = json.registrationDate;
  }


}


export interface IBadgeType {
  id:string;
  match:string;
  label:string;
  icon:string;
}


export const BadgeTypes:{[key:string]: IBadgeType} = {
  DISEASE_PREVENTION:{
    id:'disease-prevention',
    label:'Disease Prevention',
    match:'disease prevention',
    icon:'/res_sys/solutions/badges/badge disease prevention wordless.png',
  },
  NUTRITION:{
    id:'nutrition-education',
    label:'Nutrition Education',
    match:'nutrition education',
    icon:'/res_sys/solutions/badges/badge nutrition education wordless.png',
  },
  COOKING_CLASS:{
    id:'cooking-class',
    label:'Cooking Class',
    match:'cooking class',
    icon:'/res_sys/solutions/badges/badge cooking class wordless.png',
  },
  LIFE_SKILLS:{
    id:'life-skills',
    label:'Life Skills',
    match:'life skills',
    icon:'/res_sys/solutions/badges/badge life skills wordless.png',
  },
  STRESS_MANAGEMENT:{
    id:'stress-management',
    label:'Stress Management',
    match:'stress management',
    icon:'/res_sys/solutions/badges/badge stress management wordless.png',
  },
  MENTAL_HEALTH:{
    id:'mental-health',
    label:'mental health',
    match:'mental health',
    icon:'/res_sys/solutions/badges/badge mental health wordless.png',
  },
  FINANCIAL_WELL_BEING:{
    id:'financial-well-being',
    label:'Financial Well-being',
    match:'financial well-being',
    icon:'/res_sys/solutions/badges/badge financial well-being wordless.png',
  },
  FITNESS_CLASS:{
    id:'fitness-class',
    label:'Fitness Class',
    match:'fitness class',
    icon:'/res_sys/solutions/badges/badge fitness class wordless.png',
  },
  HEALTH_COACHING:{
    id:'health-coaching',
    label:'Health Coaching',
    match:'health coaching',
    icon:'/res_sys/solutions/badges/badge health coaching wordless.png',
  },
  NUTRITION_COUNSELING:{
    id:'nutrition-counseling',
    label:'Nutrition Counseling',
    match:'nutrition counseling',
    icon:'/res_sys/solutions/badges/badge nutrition counseling wordless.png',
  },
  ON_DEMAND_VIDEO:{
    id:'on-demand-video',
    label:'On-demand Video',
    match:'on-demand video',
    icon:'/res_sys/solutions/badges/badge on-demand video wordless.png',
  },
  PREVENTATIVE_SCREENING:{
    id:'preventative-screening',
    label:'Preventative Screening',
    match:'preventative screening',
    icon:'/res_sys/solutions/badges/badge preventative screening wordless.png',
  },
  SLEEP:{
    id:'sleep',
    label:'Sleep',
    match:'sleep',
    icon:'/res_sys/solutions/badges/badge sleep wordless.png',
  },
  OPTIMAL_WEIGHT:{
    id:'challenge-optimal-weight',
    label:'Optimal Weight Challenge',
    match:'optimal weight',
    icon:'/res_sys/solutions/badges/badge optimal weight wordless.png',
  },
  COOKING_SKILLS:{
    id:'challenge-cooking-skills',
    label:'Cooking Skills Challenge',
    match:'cooking skills',
    icon:'/res_sys/solutions/badges/badge cooking skills wordless.png',
  },
  HABIT_CHANGE:{
    id:'challenge-habit-change',
    label:'Habit Change Challenge',
    match:'habit change',
    icon:'/res_sys/solutions/badges/badge habit change wordless.png',
  },
  HEALTH_FAIR:{
    id:'health-fair',
    label:'Health Fair',
    match:'health fair',
    icon:'/res_sys/solutions/badges/badge health fair wordless.png',
  },
  HEALTHY_EATING:{
    id:'challenge-healthy-eating',
    label:'Healthy Eating Challenge',
    match:'healthy eating',
    icon:'/res_sys/solutions/badges/badge healthy eating wordless.png',
  },
  HEALTHY_HOLIDAY:{
    id:'challenge-healthy-holiday',
    label:'Healthy Holiday Challenge',
    match:'healthy holiday',
    icon:'/res_sys/solutions/badges/badge healthy holiday wordless.png',
  },
  MINDFULNESS:{
    id:'challenge-mindfulness',
    label:'Mindfulness Challenge',
    match:'mindfulness',
    icon:'/res_sys/solutions/badges/badge mindfulness wordless.png',
  },
  NICOTINE_FREE:{
    id:'nicotine-free',
    label:'Nicotine-free Challenge',
    match:'nicotine-free',
    icon:'/res_sys/solutions/badges/badge nicotine-free wordless.png',
  },
  OPTIMAL_HEALTH:{
    id:'optimal-health',
    label:'Optimal Health Challenge',
    match:'optimal health',
    icon:'/res_sys/solutions/badges/badge optimal health wordless.png',
  },
  OPTIMAL_SLEEP:{
    id:'optimal-sleep',
    label:'Optimal Sleep Challenge',
    match:'optimal sleep',
    icon:'/res_sys/solutions/badges/badge optimal sleep wordless.png',
  },
  UNKNOWN:{
    id:'unknown',
    label:'Unknown Badge Type',
    match:'',
    icon:'/res_sys/solutions/badges/badge health generic wordless.png',
  },
}

const badgeTypeArray = [
  BadgeTypes.DISEASE_PREVENTION,
  BadgeTypes.NUTRITION,
  BadgeTypes.COOKING_CLASS,
  BadgeTypes.LIFE_SKILLS,
  BadgeTypes.STRESS_MANAGEMENT,
  BadgeTypes.MENTAL_HEALTH,
  BadgeTypes.FINANCIAL_WELL_BEING,
  BadgeTypes.FITNESS_CLASS,
  BadgeTypes.HEALTH_COACHING,
  BadgeTypes.NUTRITION_COUNSELING,
  BadgeTypes.ON_DEMAND_VIDEO,
  BadgeTypes.PREVENTATIVE_SCREENING,
  BadgeTypes.SLEEP,
  BadgeTypes.OPTIMAL_WEIGHT,
  BadgeTypes.COOKING_SKILLS,
  BadgeTypes.HABIT_CHANGE,
  BadgeTypes.HEALTH_FAIR,
  BadgeTypes.HEALTHY_EATING,
  BadgeTypes.HEALTHY_HOLIDAY,
  BadgeTypes.MINDFULNESS,
  BadgeTypes.NICOTINE_FREE,
  BadgeTypes.OPTIMAL_HEALTH,
  BadgeTypes.OPTIMAL_SLEEP,
];


